/**
 * notificationPanelStoreCompat — Proxy backward-compatible
 *
 * Mantém a mesma API de useNotificationPanelStore mas delega para bigwaStatusPanelStore.
 * Call sites existentes (bigwaToast.ts, App.tsx, NotificationPanel) continuam funcionando.
 *
 * DEPRECATED: Migrar gradualmente para useBigwaStatusPanelStore.
 */

import { useBigwaStatusPanelStore } from './bigwaStatusPanelStore';
import type { NotificationStatus, NotificationAction, NotificationItem } from './bigwaStatusPanelStore';

// Re-export types para manter imports existentes
export type { NotificationStatus, NotificationAction, NotificationItem };

interface NotificationPanelState {
  items: NotificationItem[];
  isCollapsed: boolean;
  isVisible: boolean;
  headerTitle: string | null;
  _timerId: ReturnType<typeof setTimeout> | null;
  _isPaused: boolean;
  _lastResumeAt: number;
  _remainingMs: number;
  addItem: (item: Omit<NotificationItem, 'timestamp'>) => void;
  updateItem: (id: string, updates: { message?: string; status?: NotificationStatus; action?: NotificationAction | undefined }) => void;
  removeItem: (id: string) => void;
  clear: () => void;
  toggleCollapsed: () => void;
  dismiss: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  setHeaderTitle: (title: string | null) => void;
}

export const useNotificationPanelStore = Object.assign(
  function useNotificationPanelStoreHook<T>(selector?: (state: NotificationPanelState) => T): T {
    return useBigwaStatusPanelStore((s) => {
      const compat: NotificationPanelState = {
        items: s.items,
        isCollapsed: s.isCollapsed,
        isVisible: s.items.length > 0,
        headerTitle: s.headerTitle,
        _timerId: s._timerId,
        _isPaused: s._isPaused,
        _lastResumeAt: s._lastResumeAt,
        _remainingMs: s._remainingMs,
        addItem: s.addItem,
        updateItem: s.updateItem,
        removeItem: s.removeItem,
        clear: s.clearItems,
        toggleCollapsed: s.toggleCollapsed,
        dismiss: s.dismissItems,
        pauseTimer: s.pauseTimer,
        resumeTimer: s.resumeTimer,
        setHeaderTitle: s.setHeaderTitle,
      };

      return selector ? selector(compat) : compat as unknown as T;
    });
  },
  {
    getState: (): NotificationPanelState => {
      const s = useBigwaStatusPanelStore.getState();
      return {
        items: s.items,
        isCollapsed: s.isCollapsed,
        isVisible: s.items.length > 0,
        headerTitle: s.headerTitle,
        _timerId: s._timerId,
        _isPaused: s._isPaused,
        _lastResumeAt: s._lastResumeAt,
        _remainingMs: s._remainingMs,
        addItem: s.addItem,
        updateItem: s.updateItem,
        removeItem: s.removeItem,
        clear: s.clearItems,
        toggleCollapsed: s.toggleCollapsed,
        dismiss: s.dismissItems,
        pauseTimer: s.pauseTimer,
        resumeTimer: s.resumeTimer,
        setHeaderTitle: s.setHeaderTitle,
      };
    },
  }
);
